import { useMemo } from 'react'

import archidekt from '@/assets/svgs/archidekt.svg'
import cardsphere from '@/assets/svgs/cardsphere.svg'
import csb from '@/assets/svgs/commanderspellbook.svg'
import moxfield from '@/assets/svgs/moxfield.svg'
import mtgstocks from '@/assets/svgs/mtgstocks.svg'
import scryfall from '@/assets/svgs/scryfall.svg'
import { ButtonRoot } from '@/components/common/Button'
import { FloatingPopover } from '@/components/common/Floating'
import { LinkOutbound } from '@/components/common/Link'
import { usePage } from '@/components/providers/PageProvider'
import { type CardType } from '@/defs/card'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { parseCardId } from '@/utils/cardUtil'
import { cn, isDefined, slugify } from '@/utils/commonUtil'

import styles from './FeaturedRecOutboundBar.module.scss'
import { FeaturedRecOutboundBarMenu } from './FeaturedRecOutboundBarMenu/FeaturedRecOutboundBarMenu'

type ConfigType = {
  getLinks: (props: { cards: CardType[]; rec?: RecType }) => { href: string; name: string }[]
  label: string
  src: string
}

const searchName = (name: string) => encodeURIComponent(name).replace(/%20/g, '+')

const CONFIG_BRANDS: ConfigType[] = [
  {
    getLinks: ({ cards }) =>
      cards.map((card) => ({
        href: `https://www.cardsphere.com/search?q=${searchName(card.name)}`,
        name: card.name,
      })),
    label: 'Cardsphere',
    src: cardsphere.src,
  },
  {
    getLinks: ({ cards, rec }) => {
      const section = rec?.role === 'commander' ? 'commanders' : 'cards'
      const parts = [
        ...(rec && cards[0]?.name ? [{ name: cards[0].name, slug: parseCardId(rec.cardId).slug }] : []),
        ...(rec?.partnerRef?.cardId && cards[1]?.name
          ? [{ name: cards[1].name, slug: parseCardId(rec.partnerRef.cardId).slug }]
          : []),
      ]

      return parts.map(({ name, slug }) => ({
        href: `https://archidekt.com/${section}/${encodeURIComponent(slug)}`,
        name,
      }))
    },
    label: 'Archidekt',
    src: archidekt.src,
  },
  {
    getLinks: ({ cards }) =>
      cards.map((card) => ({ href: `https://commanderspellbook.com/search/?q=${card.name}`, name: card.name })),
    label: 'Commander Spellbook',
    src: csb.src,
  },
  {
    getLinks: ({ cards }) =>
      cards.map((card) => ({
        href: `https://moxfield.com/decks/public?format=commander&filter=${encodeURIComponent(card.name)}`,
        name: card.name,
      })),
    label: 'Moxfield',
    src: moxfield.src,
  },
  {
    getLinks: ({ cards }) =>
      cards
        .map((card) => {
          const href = card.scryfallUri
          return href ? { href, name: card.name } : undefined
        })
        .filter(isDefined),
    label: 'Scryfall',
    src: scryfall.src,
  },
  {
    getLinks: ({ cards }) =>
      cards
        .map((card) => {
          const slug = card.priceSources?.mtgstocks?.slug
          if (!slug) return undefined
          return { href: `https://www.mtgstocks.com/prints/${slug}`, name: card.name }
        })
        .filter(isDefined),
    label: 'MTG Stocks',
    src: mtgstocks.src,
  },
]

export const FeaturedRecOutboundBar = () => {
  const { page } = usePage()
  const rec = page?.featured?.rec
  const { card, cardPartner } = useCardsRec({ rec })

  const links = useMemo(() => {
    if (!rec || !card) return []
    const cards = [card, cardPartner].filter(isDefined)

    return CONFIG_BRANDS.map(({ getLinks, ...rest }) => ({
      ...rest,
      links: getLinks({ cards, rec }).filter(isDefined),
    })).filter(({ links }) => links.length > 0)
  }, [card, cardPartner, rec])

  if (!card) return null

  return (
    <div className={styles.container}>
      <ul className={styles.list}>
        {links.map(({ label, links, src }) => {
          const className = cn(styles.link, { link: slugify(label), styles })
          const icon = <img alt={label} decoding='async' loading='lazy' src={src} />

          return (
            <li key={label}>
              {links.length === 1 ? (
                <LinkOutbound className={className} href={links[0]?.href || ''} utm={{ medium: 'featured' }}>
                  {icon}
                </LinkOutbound>
              ) : (
                <FloatingPopover content={<FeaturedRecOutboundBarMenu links={links} />}>
                  <ButtonRoot aria-label={label} className={className}>
                    {icon}
                  </ButtonRoot>
                </FloatingPopover>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
